// services/functions/listRecentUnlockCodes.ts — typed wrapper
// around the listRecentUnlockCodes callable. Returns the most
// recent N codes' sanitised summaries (hash prefix only; plaintext
// is present only when STORE_UNLOCK_CODE_PLAINTEXT=true on the mint
// function, otherwise the row shows the hash prefix with a "plaintext
// not stored" affordance).

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';

interface Input {
  limit: number;
}

export interface RecentUnlockCode {
  /** Plaintext code; absent on older codes where storage was off. */
  code?: string;
  /** First 12 hex chars of the SHA-256 hash of the plaintext. */
  codeHashPrefix: string;
  productIds: string[];
  /** Server-collapsed: 'active' | 'revoked' | 'exhausted'. */
  status: string;
  maxUses: number;
  usedCount: number;
  /** ISO 8601. */
  createdAt: string;
  /** ISO 8601; absent = no expiry. */
  expiresAt?: string;
  note?: string;
  /** Set only after a successful revokeUnlockCode call. */
  revokedAt?: string | null;
  revokedBy?: string | null;
  revokeReason?: string | null;
  /** Denormalised mirror of the most recent entry in the redemptions
   *  subcollection. Null until usedCount > 0. */
  lastRedeemedAt?: string | null;
  lastRedeemedBy?: string | null;
  lastRedeemedOwnerKind?: 'uid' | 'install' | null;
  /** Signed-in caller's uid at last redemption, independent of
   *  entitlement scope. Null when the redeemer was anonymous. */
  lastRedeemedByUid?: string | null;
}

interface Output {
  codes: RecentUnlockCode[];
}

const callable = httpsCallable<Input, Output>(
  functions,
  'listRecentUnlockCodes',
);

export async function listRecentUnlockCodes(
  input: Input,
): Promise<RecentUnlockCode[]> {
  const res = await callable(input);
  return res.data.codes ?? [];
}
